import React, { useState, useEffect } from 'react';
import {
  User, Mail, Check, X, Loader2, DollarSign, Trash2, Plus, Moon, Sun,
  ShieldCheck, Copy, ExternalLink, Settings, Link2, SlidersHorizontal, ChevronDown,
} from 'lucide-react';
import { authAPI, linkedEmailAPI } from '../api/client';
import { CATEGORIES } from '../constants/categories';
import { useAnalytics } from '../hooks/useAnalytics';
import { useTheme, useTokens } from '../context/ThemeContext';
import ForwardingSetupModal from './ForwardingSetupModal';
import { TOPBAR_HEIGHT } from './Topbar';

const SUPPORTED_CURRENCIES = [
  { code: 'INR', label: 'Indian Rupee (₹)' },
  { code: 'USD', label: 'US Dollar ($)' },
  { code: 'EUR', label: 'Euro (€)' },
  { code: 'GBP', label: 'British Pound (£)' },
  { code: 'JPY', label: 'Japanese Yen (¥)' },
  { code: 'AUD', label: 'Australian Dollar (A$)' },
  { code: 'CAD', label: 'Canadian Dollar (C$)' },
  { code: 'CHF', label: 'Swiss Franc (CHF)' },
  { code: 'SGD', label: 'Singapore Dollar (S$)' },
  { code: 'AED', label: 'UAE Dirham (AED)' },
  { code: 'SAR', label: 'Saudi Riyal (SAR)' },
  { code: 'MYR', label: 'Malaysian Ringgit (MYR)' },
  { code: 'THB', label: 'Thai Baht (THB)' },
  { code: 'IDR', label: 'Indonesian Rupiah (IDR)' },
  { code: 'PHP', label: 'Philippine Peso (₱)' },
  { code: 'KRW', label: 'South Korean Won (₩)' },
  { code: 'CNY', label: 'Chinese Yuan (¥)' },
  { code: 'HKD', label: 'Hong Kong Dollar (HK$)' },
  { code: 'VND', label: 'Vietnamese Dong (₫)' },
  { code: 'TWD', label: 'Taiwan Dollar (NT$)' },
  { code: 'NZD', label: 'New Zealand Dollar (NZ$)' },
  { code: 'MXN', label: 'Mexican Peso (MX$)' },
  { code: 'BRL', label: 'Brazilian Real (R$)' },
  { code: 'ZAR', label: 'South African Rand (R)' },
  { code: 'NOK', label: 'Norwegian Krone (kr)' },
  { code: 'SEK', label: 'Swedish Krona (kr)' },
  { code: 'DKK', label: 'Danish Krone (kr)' },
  { code: 'NGN', label: 'Nigerian Naira (₦)' },
  { code: 'KES', label: 'Kenyan Shilling (KSh)' },
  { code: 'EGP', label: 'Egyptian Pound (E£)' },
  { code: 'PKR', label: 'Pakistani Rupee (₨)' },
  { code: 'BDT', label: 'Bangladeshi Taka (৳)' },
];

const NAV_ITEMS = [
  { id: 'profile',      label: 'Profile',      Icon: User },
  { id: 'account',      label: 'Account',      Icon: Settings },
  { id: 'integrations', label: 'Integrations', Icon: Link2 },
  { id: 'preferences',  label: 'Preferences',  Icon: SlidersHorizontal },
];

function SectionTitle({ children }) {
  const t = useTokens();
  return (
    <h3 style={{ fontSize: '13px', fontWeight: '600', color: t.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 16px 0' }}>
      {children}
    </h3>
  );
}

function Card({ children, danger }) {
  const t = useTokens();
  return (
    <div style={{
      backgroundColor: t.surface,
      borderRadius: '12px',
      padding: '20px',
      border: `1px solid ${danger ? t.danger : t.border}`,
      marginBottom: '16px',
    }}>
      {children}
    </div>
  );
}

// ─── Profile Panel ────────────────────────────────────────────────────────────
function ProfilePanel({ user, onUserUpdate }) {
  const t = useTokens();
  const { track } = useAnalytics();
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(user?.name || '');
  const [saving, setSaving] = useState(false);
  const spinning = { animation: 'spin 1s linear infinite' };

  const initials = user?.name
    ? user.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
    : null;

  const handleSaveName = async () => {
    if (!editedName.trim()) return;
    setSaving(true);
    try {
      const res = await authAPI.updateProfile({ name: editedName.trim() });
      if (onUserUpdate) onUserUpdate(res.data);
      track('profile_name_updated');
      setIsEditingName(false);
    } catch (e) {
      console.error('Failed to save name:', e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <SectionTitle>Profile</SectionTitle>

      <Card>
        {/* Avatar placeholder */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
          <div style={{
            width: '64px', height: '64px', borderRadius: '50%',
            backgroundColor: t.avatarBg, color: t.avatarText,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '20px', fontWeight: '700', flexShrink: 0,
          }}>
            {initials || <User size={28} />}
          </div>
          <div>
            <p style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: t.text }}>
              {user?.name || user?.email?.split('@')[0] || 'User'}
            </p>
            <p style={{ margin: '2px 0 0', fontSize: '13px', color: t.textSecondary }}>{user?.email}</p>
          </div>
        </div>

        <div style={{ borderTop: `1px solid ${t.borderLight}`, paddingTop: '16px' }}>
          {/* Name */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '12px', color: t.textSecondary, marginBottom: '6px' }}>Display name</label>
            {isEditingName ? (
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  value={editedName}
                  onChange={e => setEditedName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSaveName()}
                  style={{ flex: 1, padding: '8px 12px', border: `1px solid ${t.inputBorder}`, borderRadius: '8px', fontSize: '14px', outline: 'none', backgroundColor: t.inputBg, color: t.text }}
                  autoFocus
                />
                <button onClick={handleSaveName} disabled={saving} style={{ width: '32px', height: '36px', borderRadius: '8px', border: 'none', backgroundColor: t.successBg, color: t.success, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {saving ? <Loader2 size={15} style={spinning} /> : <Check size={15} />}
                </button>
                <button onClick={() => { setIsEditingName(false); setEditedName(user?.name || ''); }} style={{ width: '32px', height: '36px', borderRadius: '8px', border: 'none', backgroundColor: t.chipBg, color: t.chipText, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <X size={15} />
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '14px', color: t.text }}>{user?.name || '—'}</span>
                <button onClick={() => setIsEditingName(true)} style={{ background: 'none', border: 'none', color: t.textSecondary, fontSize: '13px', cursor: 'pointer' }}>Edit</button>
              </div>
            )}
          </div>

          {/* Email — read-only here */}
          <div>
            <label style={{ display: 'block', fontSize: '12px', color: t.textSecondary, marginBottom: '6px' }}>Email address</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Mail size={14} color={t.textSecondary} />
              <span style={{ fontSize: '14px', color: t.text }}>{user?.email || '—'}</span>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

// ─── Account Panel ────────────────────────────────────────────────────────────
function AccountPanel({ user, onLogout }) {
  const t = useTokens();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const spinning = { animation: 'spin 1s linear infinite' };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await authAPI.deleteAccount();
      onLogout();
    } catch (e) {
      console.error('Failed to delete account:', e);
      setDeleting(false);
    }
  };

  return (
    <div>
      <SectionTitle>Account</SectionTitle>

      <Card>
        <label style={{ display: 'block', fontSize: '12px', color: t.textSecondary, marginBottom: '6px' }}>Email address</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Mail size={14} color={t.textSecondary} />
          <span style={{ fontSize: '14px', color: t.text }}>{user?.email || '—'}</span>
        </div>
      </Card>

      {/* Danger zone */}
      <Card danger>
        <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600', color: t.danger }}>Delete Account</h4>
        <p style={{ fontSize: '13px', color: t.textSecondary, margin: '0 0 14px 0', lineHeight: '1.5' }}>
          Permanently delete your account and all associated data — transactions, budgets, goals, and insights. This cannot be undone.
        </p>
        <button
          onClick={() => setDeleteModalOpen(true)}
          style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 16px', border: `1px solid ${t.danger}`, borderRadius: '8px', backgroundColor: 'transparent', color: t.danger, fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}
        >
          <Trash2 size={14} /> Delete Account
        </button>
      </Card>

      {/* Delete confirmation modal */}
      {deleteModalOpen && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '16px' }}>
          <div style={{ backgroundColor: t.surface, borderRadius: '16px', padding: '28px', width: '100%', maxWidth: '420px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: t.danger, margin: '0 0 12px 0' }}>Delete Account</h3>
            <p style={{ fontSize: '14px', color: t.text, margin: '0 0 8px 0', lineHeight: '1.5' }}>
              This will permanently delete your account and all data including:
            </p>
            <ul style={{ fontSize: '14px', color: t.textSecondary, margin: '0 0 12px 0', paddingLeft: '20px', lineHeight: '1.8' }}>
              <li>All transactions</li>
              <li>Budgets and goals</li>
              <li>AI-generated insights</li>
              <li>Email connections (Gmail / Outlook)</li>
            </ul>
            <p style={{ fontSize: '14px', color: t.text, margin: '0 0 8px 0' }}>
              Type <strong>DELETE</strong> to confirm.
            </p>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={e => setDeleteConfirmText(e.target.value)}
              placeholder="Type DELETE to confirm"
              style={{ width: '100%', padding: '10px 12px', border: `1px solid ${t.dangerBorder}`, borderRadius: '8px', fontSize: '14px', outline: 'none', marginTop: '4px', boxSizing: 'border-box', backgroundColor: t.inputBg, color: t.text }}
              autoFocus
            />
            <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
              <button
                onClick={() => { setDeleteModalOpen(false); setDeleteConfirmText(''); }}
                disabled={deleting}
                style={{ flex: 1, padding: '11px', borderRadius: '10px', border: `1px solid ${t.border}`, backgroundColor: t.surface, color: t.text, fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteConfirmText !== 'DELETE' || deleting}
                style={{ flex: 1, padding: '11px', borderRadius: '10px', border: 'none', backgroundColor: '#ef4444', color: 'white', fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', opacity: deleteConfirmText !== 'DELETE' || deleting ? 0.5 : 1, cursor: deleteConfirmText !== 'DELETE' || deleting ? 'not-allowed' : 'pointer' }}
              >
                {deleting ? <><Loader2 size={15} style={spinning} />Deleting…</> : 'Delete My Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Integrations Panel ───────────────────────────────────────────────────────
function IntegrationsPanel({ user }) {
  const t = useTokens();
  const { track } = useAnalytics();
  const isMobile = window.innerWidth < 480;
  const spinning = { animation: 'spin 1s linear infinite' };

  const [linkedEmails, setLinkedEmails] = useState([]);
  const [inboxAddress, setInboxAddress] = useState(null);
  const [linkedEmailsLoaded, setLinkedEmailsLoaded] = useState(false);
  const [addingLinkedEmail, setAddingLinkedEmail] = useState(false);
  const [linkedEmailInput, setLinkedEmailInput] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [linkedEmailError, setLinkedEmailError] = useState('');
  const [linkedEmailLoading, setLinkedEmailLoading] = useState(false);
  const [removingEmail, setRemovingEmail] = useState(null);
  const [copiedAddress, setCopiedAddress] = useState('');
  const [forwardingModalOpen, setForwardingModalOpen] = useState(false);
  const [forwardingModalEmail, setForwardingModalEmail] = useState('');

  useEffect(() => {
    linkedEmailAPI.getAll()
      .then(res => {
        setLinkedEmails(res.data.linkedEmails || []);
        setInboxAddress(res.data.inboxAddress || null);
      })
      .catch(() => {})
      .finally(() => setLinkedEmailsLoaded(true));
  }, []);

  const handleCopyAddress = (address) => {
    navigator.clipboard.writeText(address).then(() => {
      setCopiedAddress(address);
      setTimeout(() => setCopiedAddress(''), 2000);
    });
  };

  const handleRequestOTP = async () => {
    setLinkedEmailError('');
    setLinkedEmailLoading(true);
    try {
      await linkedEmailAPI.requestOTP(linkedEmailInput.trim());
      setOtpSent(true);
    } catch (err) {
      setLinkedEmailError(err.response?.data?.error || 'Failed to send code');
    } finally {
      setLinkedEmailLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    setLinkedEmailError('');
    setLinkedEmailLoading(true);
    try {
      const res = await linkedEmailAPI.verify(linkedEmailInput.trim(), otpCode.trim());
      setLinkedEmails(res.data.linkedEmails || []);
      if (res.data.inboxAddress) setInboxAddress(res.data.inboxAddress);
      setAddingLinkedEmail(false);
      setOtpSent(false);
      setLinkedEmailInput('');
      setOtpCode('');
      track('linked_email_added');
    } catch (err) {
      setLinkedEmailError(err.response?.data?.error || 'Invalid or expired code');
    } finally {
      setLinkedEmailLoading(false);
    }
  };

  const handleCancelLinkedEmail = () => {
    setAddingLinkedEmail(false);
    setOtpSent(false);
    setLinkedEmailInput('');
    setOtpCode('');
    setLinkedEmailError('');
  };

  const handleRemoveLinkedEmail = async (email) => {
    setRemovingEmail(email);
    try {
      const res = await linkedEmailAPI.remove(email);
      setLinkedEmails(res.data.linkedEmails || []);
      track('linked_email_removed');
    } catch (err) {
      console.error('Failed to remove linked email:', err);
    } finally {
      setRemovingEmail(null);
    }
  };

  // Build display list: always show user's own email first (as primary), then linked emails
  const primaryEmail = user?.email;
  const allEmails = [
    { email: primaryEmail, isPrimary: true },
    ...linkedEmails.filter(e => e.email !== primaryEmail),
  ];

  return (
    <div>
      <SectionTitle>Integrations</SectionTitle>

      {/* Nura Inbox Address — always visible */}
      <Card>
        <label style={{ display: 'block', fontSize: '12px', color: t.textSecondary, marginBottom: '8px' }}>Your Nura inbox address</label>
        <p style={{ fontSize: '12px', color: t.textMuted, margin: '0 0 10px 0', lineHeight: '1.5' }}>
          Forward bank notification emails to this address so Nura can process them.
        </p>
        {inboxAddress ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: t.surfaceAlt, border: `1px solid ${t.border}`, borderRadius: '8px', padding: '9px 12px' }}>
              <span style={{ flex: 1, fontSize: '13px', fontFamily: 'monospace', color: t.text, wordBreak: 'break-all' }}>{inboxAddress}</span>
              <button
                onClick={() => handleCopyAddress(inboxAddress)}
                title="Copy"
                style={{ flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer', color: copiedAddress === inboxAddress ? t.success : t.textSecondary, display: 'flex', alignItems: 'center', padding: '2px' }}
              >
                {copiedAddress === inboxAddress ? <Check size={14} /> : <Copy size={14} />}
              </button>
            </div>
            <button
              onClick={() => { setForwardingModalEmail(inboxAddress); setForwardingModalOpen(true); }}
              style={{ marginTop: '8px', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '12px', color: t.textSecondary, display: 'flex', alignItems: 'center', gap: '4px', textDecoration: 'underline', textUnderlineOffset: '2px' }}
            >
              <ExternalLink size={12} /> How to set up forwarding
            </button>
          </>
        ) : (
          <p style={{ fontSize: '13px', color: t.textMuted, margin: 0, fontStyle: 'italic' }}>
            Loading inbox address…
          </p>
        )}
      </Card>

      {/* Linked Emails */}
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '12px', color: t.textSecondary, marginBottom: '2px' }}>Linked emails</label>
            <p style={{ fontSize: '12px', color: t.textMuted, margin: 0 }}>Emails Nura monitors for bank notifications</p>
          </div>
          {!addingLinkedEmail && (
            <button
              onClick={() => setAddingLinkedEmail(true)}
              style={{ display: 'flex', alignItems: 'center', gap: '4px', backgroundColor: 'transparent', border: 'none', color: t.textSecondary, fontSize: '13px', cursor: 'pointer', padding: '2px 4px', flexShrink: 0 }}
            >
              <Plus size={14} /> Add
            </button>
          )}
        </div>

        {addingLinkedEmail && (
          <div style={{ marginBottom: '12px' }}>
            {!otpSent ? (
              <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '8px' }}>
                <input
                  type="email"
                  value={linkedEmailInput}
                  onChange={e => { setLinkedEmailInput(e.target.value); setLinkedEmailError(''); }}
                  onKeyDown={e => e.key === 'Enter' && handleRequestOTP()}
                  placeholder="email@example.com"
                  style={{ flex: 1, width: isMobile ? '100%' : undefined, boxSizing: 'border-box', padding: '8px 12px', border: `1px solid ${linkedEmailError ? '#ef4444' : t.inputBorder}`, borderRadius: '8px', fontSize: '14px', outline: 'none', backgroundColor: t.inputBg, color: t.text }}
                  autoFocus
                />
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={handleRequestOTP}
                    disabled={linkedEmailLoading || !linkedEmailInput.trim()}
                    style={{ flex: 1, padding: '8px 14px', borderRadius: '8px', border: 'none', backgroundColor: t.successBg, color: t.success, cursor: 'pointer', fontSize: '13px', fontWeight: '500', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', opacity: linkedEmailLoading || !linkedEmailInput.trim() ? 0.5 : 1 }}
                  >
                    {linkedEmailLoading ? <Loader2 size={14} style={spinning} /> : 'Send code'}
                  </button>
                  <button onClick={handleCancelLinkedEmail} style={{ width: '36px', height: '36px', flexShrink: 0, borderRadius: '8px', border: 'none', backgroundColor: t.chipBg, color: t.chipText, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <X size={16} />
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <p style={{ fontSize: '13px', color: t.textSecondary, margin: 0 }}>
                  Enter the 6-digit code sent to <strong>{linkedEmailInput}</strong>
                </p>
                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '8px' }}>
                  <input
                    type="text"
                    value={otpCode}
                    onChange={e => { setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setLinkedEmailError(''); }}
                    onKeyDown={e => e.key === 'Enter' && otpCode.length === 6 && handleVerifyOTP()}
                    placeholder="000000"
                    maxLength={6}
                    style={{ flex: 1, boxSizing: 'border-box', padding: '8px 12px', border: `1px solid ${linkedEmailError ? '#ef4444' : t.inputBorder}`, borderRadius: '8px', fontSize: '20px', letterSpacing: '6px', outline: 'none', backgroundColor: t.inputBg, color: t.text, fontFamily: 'monospace' }}
                    autoFocus
                  />
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={handleVerifyOTP}
                      disabled={linkedEmailLoading || otpCode.length !== 6}
                      style={{ flex: 1, padding: '8px 14px', borderRadius: '8px', border: 'none', backgroundColor: t.successBg, color: t.success, cursor: 'pointer', fontSize: '13px', fontWeight: '500', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', opacity: linkedEmailLoading || otpCode.length !== 6 ? 0.5 : 1 }}
                    >
                      {linkedEmailLoading ? <Loader2 size={16} style={spinning} /> : <><Check size={14} /> Verify</>}
                    </button>
                    <button onClick={handleCancelLinkedEmail} style={{ width: '36px', height: '36px', flexShrink: 0, borderRadius: '8px', border: 'none', backgroundColor: t.chipBg, color: t.chipText, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <X size={16} />
                    </button>
                  </div>
                </div>
                <button
                  onClick={() => { setOtpSent(false); setOtpCode(''); setLinkedEmailError(''); }}
                  style={{ background: 'none', border: 'none', color: t.textSecondary, fontSize: '12px', cursor: 'pointer', padding: 0, textAlign: 'left' }}
                >
                  Change email
                </button>
              </div>
            )}
            {linkedEmailError && (
              <p style={{ fontSize: '12px', color: '#ef4444', margin: '6px 0 0' }}>{linkedEmailError}</p>
            )}
          </div>
        )}

        {linkedEmailsLoaded && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {allEmails.map(entry => (
              <div key={entry.email} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: t.surfaceAlt, borderRadius: '8px', padding: '10px 12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <ShieldCheck size={15} color={t.success} />
                  <div>
                    <span style={{ fontSize: '13px', color: t.text }}>{entry.email}</span>
                    {entry.isPrimary && (
                      <span style={{ marginLeft: '8px', fontSize: '11px', color: t.textMuted, backgroundColor: t.chipBg, borderRadius: '4px', padding: '1px 6px' }}>primary</span>
                    )}
                  </div>
                </div>
                {!entry.isPrimary && (
                  <button
                    onClick={() => handleRemoveLinkedEmail(entry.email)}
                    disabled={removingEmail === entry.email}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.textMuted, display: 'flex', alignItems: 'center', padding: '2px' }}
                  >
                    {removingEmail === entry.email ? <Loader2 size={14} style={spinning} /> : <X size={14} />}
                  </button>
                )}
              </div>
            ))}
            {allEmails.length === 1 && !addingLinkedEmail && (
              <p style={{ fontSize: '12px', color: t.textMuted, margin: '4px 0 0' }}>
                Click Add to link additional emails for bank notification forwarding.
              </p>
            )}
          </div>
        )}
      </Card>

      <ForwardingSetupModal
        open={forwardingModalOpen}
        onClose={() => setForwardingModalOpen(false)}
        ingestionAddress={forwardingModalEmail}
      />
    </div>
  );
}

// ─── Preferences Panel ────────────────────────────────────────────────────────
function PreferencesPanel({ user, onUserUpdate }) {
  const t = useTokens();
  const { theme, toggleTheme } = useTheme();
  const { track } = useAnalytics();
  const spinning = { animation: 'spin 1s linear infinite' };

  const [selectedCurrency, setSelectedCurrency] = useState(user?.baseCurrency || 'INR');
  const [currencySaved, setCurrencySaved] = useState(false);

  const [customCategories, setCustomCategories] = useState(user?.customCategories || []);
  const [newCategory, setNewCategory] = useState('');
  const [addingCategory, setAddingCategory] = useState(false);
  const [savingCategories, setSavingCategories] = useState(false);

  const [excludedCategories, setExcludedCategories] = useState(user?.excludedCategories || []);
  const [savingExcluded, setSavingExcluded] = useState(false);

  const handleCurrencyChange = async (newCurrency) => {
    setSelectedCurrency(newCurrency);
    setCurrencySaved(false);
    try {
      const res = await authAPI.updateProfile({ baseCurrency: newCurrency });
      if (onUserUpdate) onUserUpdate(res.data);
      track('currency_changed', { currency: newCurrency });
      setCurrencySaved(true);
      setTimeout(() => setCurrencySaved(false), 2000);
    } catch (e) {
      console.error('Failed to save currency:', e);
    }
  };

  const handleAddCategory = async () => {
    const trimmed = newCategory.trim();
    if (!trimmed) return;
    const updated = [...customCategories, trimmed];
    setSavingCategories(true);
    try {
      const res = await authAPI.updateProfile({ customCategories: updated });
      setCustomCategories(res.data.customCategories || updated);
      if (onUserUpdate) onUserUpdate(res.data);
      track('custom_category_added');
      setNewCategory('');
      setAddingCategory(false);
    } catch (e) {
      console.error('Failed to save custom category:', e);
    } finally {
      setSavingCategories(false);
    }
  };

  const handleRemoveCategory = async (index) => {
    const updated = customCategories.filter((_, i) => i !== index);
    setSavingCategories(true);
    try {
      const res = await authAPI.updateProfile({ customCategories: updated });
      setCustomCategories(res.data.customCategories || updated);
      if (onUserUpdate) onUserUpdate(res.data);
      track('custom_category_removed');
    } catch (e) {
      console.error('Failed to remove custom category:', e);
    } finally {
      setSavingCategories(false);
    }
  };

  const handleToggleExcluded = async (category) => {
    const isExcluded = excludedCategories.includes(category);
    const updated = isExcluded
      ? excludedCategories.filter(c => c !== category)
      : [...excludedCategories, category];
    setSavingExcluded(true);
    try {
      const res = await authAPI.updateProfile({ excludedCategories: updated });
      const saved = res.data.excludedCategories || updated;
      setExcludedCategories(saved);
      if (onUserUpdate) onUserUpdate(res.data);
    } catch (e) {
      console.error('Failed to update excluded categories:', e);
    } finally {
      setSavingExcluded(false);
    }
  };

  return (
    <div>
      <SectionTitle>Preferences</SectionTitle>

      {/* Appearance */}
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <label style={{ display: 'block', fontSize: '12px', color: t.textSecondary, marginBottom: '4px' }}>Appearance</label>
            <span style={{ fontSize: '14px', color: t.text, fontWeight: '500' }}>{theme === 'dark' ? 'Dark' : 'Light'} mode</span>
          </div>
          <button
            onClick={toggleTheme}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 14px', border: `1px solid ${t.border}`, borderRadius: '10px', backgroundColor: t.chipBg, color: t.text, fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}
          >
            {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            {theme === 'dark' ? 'Light' : 'Dark'}
          </button>
        </div>
      </Card>

      {/* Base Currency */}
      <Card>
        <label style={{ display: 'block', fontSize: '12px', color: t.textSecondary, marginBottom: '8px' }}>Base currency</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <DollarSign size={16} color={t.textSecondary} />
          <select
            value={selectedCurrency}
            onChange={e => handleCurrencyChange(e.target.value)}
            style={{ border: `1px solid ${t.inputBorder}`, borderRadius: '8px', padding: '7px 10px', fontSize: '14px', color: t.text, backgroundColor: t.inputBg, outline: 'none', cursor: 'pointer' }}
          >
            {SUPPORTED_CURRENCIES.map(c => (
              <option key={c.code} value={c.code}>{c.label}</option>
            ))}
          </select>
          {currencySaved && <Check size={16} color={t.success} />}
        </div>
        <p style={{ fontSize: '12px', color: t.textSecondary, marginTop: '10px', marginBottom: 0 }}>
          Transactions in other currencies will be converted to this currency when synced.
        </p>
      </Card>

      {/* Custom Categories */}
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
          <label style={{ fontSize: '12px', color: t.textSecondary }}>Custom categories</label>
          {!addingCategory && (
            <button onClick={() => setAddingCategory(true)} style={{ display: 'flex', alignItems: 'center', gap: '4px', backgroundColor: 'transparent', border: 'none', color: t.textSecondary, fontSize: '13px', cursor: 'pointer', padding: '2px 4px' }}>
              <Plus size={14} /> Add
            </button>
          )}
        </div>
        {addingCategory && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <input
              type="text"
              value={newCategory}
              onChange={e => setNewCategory(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAddCategory()}
              placeholder="Category name"
              style={{ flex: 1, padding: '8px 12px', border: `1px solid ${t.inputBorder}`, borderRadius: '8px', fontSize: '14px', outline: 'none', backgroundColor: t.inputBg, color: t.text }}
              autoFocus
            />
            <button onClick={handleAddCategory} disabled={savingCategories || !newCategory.trim()} style={{ width: '32px', height: '36px', borderRadius: '8px', border: 'none', backgroundColor: t.successBg, color: t.success, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {savingCategories ? <Loader2 size={15} style={spinning} /> : <Check size={15} />}
            </button>
            <button onClick={() => { setAddingCategory(false); setNewCategory(''); }} style={{ width: '32px', height: '36px', borderRadius: '8px', border: 'none', backgroundColor: t.chipBg, color: t.chipText, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <X size={15} />
            </button>
          </div>
        )}
        {customCategories.length === 0 && !addingCategory ? (
          <p style={{ fontSize: '13px', color: t.textMuted, margin: 0 }}>No custom categories. Click Add to create one.</p>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {customCategories.map((cat, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: t.chipBg, borderRadius: '20px', padding: '4px 10px 4px 12px', fontSize: '13px', color: t.chipText }}>
                <span style={{ lineHeight: 1 }}>{cat}</span>
                <button onClick={() => handleRemoveCategory(i)} disabled={savingCategories} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', cursor: 'pointer', color: t.textMuted, padding: 0, lineHeight: 1 }}>
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Excluded from expenses */}
      <Card>
        <label style={{ display: 'block', fontSize: '12px', color: t.textSecondary, marginBottom: '4px' }}>Exclude from expense totals</label>
        <p style={{ fontSize: '12px', color: t.textMuted, margin: '0 0 12px 0' }}>
          Transactions in these categories won't count toward your expense totals or charts.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {[...CATEGORIES, ...customCategories].map((cat) => {
            const isExcluded = excludedCategories.includes(cat);
            return (
              <button
                key={cat}
                onClick={() => handleToggleExcluded(cat)}
                disabled={savingExcluded}
                style={{
                  display: 'flex', alignItems: 'center', gap: '5px',
                  padding: '4px 10px',
                  borderRadius: '20px',
                  border: `1px solid ${isExcluded ? t.danger : t.border}`,
                  backgroundColor: isExcluded ? t.dangerBg || '#fef2f2' : t.chipBg,
                  color: isExcluded ? t.danger || '#ef4444' : t.chipText,
                  fontSize: '13px', cursor: 'pointer', fontWeight: isExcluded ? '600' : '400',
                }}
              >
                {isExcluded && <Check size={12} />}
                {cat}
              </button>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

// ─── Main Modal ───────────────────────────────────────────────────────────────
function SettingsModal({ open, onClose, user, onUserUpdate, onLogout, initialSection, renderMobileNav }) {
  const t = useTokens();
  const [activeSection, setActiveSection] = useState(initialSection || 'profile');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => {
    if (open) setActiveSection(initialSection || 'profile');
  }, [open, initialSection]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Prevent body scroll while modal is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  // ── Mobile layout: full-screen accordion ─────────────────────────────────────
  if (isMobile) {
    const panelMap = {
      profile:      <ProfilePanel user={user} onUserUpdate={onUserUpdate} />,
      account:      <AccountPanel user={user} onLogout={onLogout} />,
      integrations: <IntegrationsPanel user={user} />,
      preferences:  <PreferencesPanel user={user} onUserUpdate={onUserUpdate} />,
    };
    return (
      <div style={{ position: 'fixed', top: TOPBAR_HEIGHT, left: 0, right: 0, bottom: 0, zIndex: 21, display: 'flex', flexDirection: 'column', backgroundColor: t.bg }}>
        {/* Scrollable accordion content */}
        <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {NAV_ITEMS.map(({ id, label, Icon }) => {
            const isOpen = activeSection === id;
            return (
              <div key={id} style={{ borderBottom: `1px solid ${t.border}` }}>
                <button
                  onClick={() => setActiveSection(isOpen ? null : id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    padding: '16px 20px',
                    border: 'none',
                    backgroundColor: t.surface,
                    color: isOpen ? t.navActiveText : t.text,
                    cursor: 'pointer',
                    gap: '12px',
                    textAlign: 'left',
                  }}
                >
                  <Icon size={18} strokeWidth={isOpen ? 2.2 : 1.8} />
                  <span style={{ flex: 1, fontSize: '15px', fontWeight: isOpen ? '600' : '500' }}>{label}</span>
                  <ChevronDown size={16} style={{ color: t.textMuted, transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'none' }} />
                </button>
                {isOpen && (
                  <div style={{ padding: '4px 16px 20px', backgroundColor: t.bg }}>
                    {panelMap[id]}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Bottom nav */}
        {renderMobileNav?.()}
      </div>
    );
  }

  // ── Desktop layout: centered modal with LHS nav ──────────────────────────────
  return (
    <div
      style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '24px' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        backgroundColor: t.surface,
        borderRadius: '16px',
        width: '100%',
        maxWidth: '760px',
        height: 'min(600px, 90vh)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: `1px solid ${t.border}`, flexShrink: 0 }}>
          <h2 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: t.text }}>Settings</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.textSecondary, display: 'flex', alignItems: 'center', padding: '4px', borderRadius: '6px' }}>
            <X size={18} />
          </button>
        </div>

        {/* Body: LHS nav + content */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* LHS navigation */}
          <nav style={{ width: '180px', flexShrink: 0, borderRight: `1px solid ${t.border}`, padding: '16px 8px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {NAV_ITEMS.map(({ id, label, Icon }) => {
              const isActive = activeSection === id;
              return (
                <button
                  key={id}
                  onClick={() => setActiveSection(id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    width: '100%',
                    padding: '9px 12px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: isActive ? t.navActive : 'transparent',
                    color: isActive ? t.navActiveText : t.textSecondary,
                    fontSize: '14px',
                    fontWeight: isActive ? '600' : '400',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'background-color 0.1s',
                  }}
                >
                  <Icon size={15} />
                  {label}
                </button>
              );
            })}
          </nav>

          {/* Content area */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
            {activeSection === 'profile' && <ProfilePanel user={user} onUserUpdate={onUserUpdate} />}
            {activeSection === 'account' && <AccountPanel user={user} onLogout={onLogout} />}
            {activeSection === 'integrations' && <IntegrationsPanel user={user} />}
            {activeSection === 'preferences' && <PreferencesPanel user={user} onUserUpdate={onUserUpdate} />}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SettingsModal;
